import API_BASE_URL from '../config';
import { getAuthHeaders } from '../utils/authHeaders';
import { fetchWithAuth } from '../utils/fetchWithAuth';

export interface TenantInfo {
  id: string;
  name: string;
  subdomain: string;
  description: string;
  entitlements?: string[];
}

export const ENTITLEMENT_ADVANCED_ANALYTICS = 'advanced_analytics';

/** ASCII case-fold, matching Rust `eq_ignore_ascii_case`. */
function eqIgnoreAsciiCase(left: string, right: string): boolean {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    let a = left.charCodeAt(i);
    let b = right.charCodeAt(i);
    if (a >= 65 && a <= 90) a += 32;
    if (b >= 65 && b <= 90) b += 32;
    if (a !== b) return false;
  }
  return true;
}

export function tenantHasEntitlement(
  tenant: { entitlements?: readonly string[] | null } | null | undefined,
  key: string,
): boolean {
  const entitlements = tenant?.entitlements;
  if (!entitlements || entitlements.length === 0) return false;
  return entitlements.some((item) => typeof item === 'string' && eqIgnoreAsciiCase(item, key));
}

export function hasAdvancedAnalyticsEntitlement(
  tenant: { entitlements?: readonly string[] | null } | null | undefined,
): boolean {
  return tenantHasEntitlement(tenant, ENTITLEMENT_ADVANCED_ANALYTICS);
}

class TenantService {
  private cachedTenant: TenantInfo | null = null;
  private cachedAtMs: number = 0;
  private inflightRequest: Promise<TenantInfo | null> | null = null;
  private readonly CACHE_TTL_MS: number = 5 * 60 * 1000; // 5 minutes

  private getCacheStorageKey(): string {
    try {
      const userInfoRaw = localStorage.getItem('oidc_user_info');
      if (userInfoRaw) {
        const parsed = JSON.parse(userInfoRaw);
        if (parsed && parsed.userId) {
          return `tenant_info_cache_${parsed.userId}`;
        }
      }
    } catch (_) {
      // ignore
    }
    return 'tenant_info_cache';
  }

  private readCacheFromStorage(): TenantInfo | null {
    try {
      const key = this.getCacheStorageKey();
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { data: TenantInfo; timestamp: number };
      if (parsed && parsed.data && typeof parsed.timestamp === 'number') {
        // honor TTL
        if (Date.now() - parsed.timestamp < this.CACHE_TTL_MS) {
          this.cachedTenant = parsed.data;
          this.cachedAtMs = parsed.timestamp;
          return parsed.data;
        }
      }
    } catch (_) {
      // ignore parse/storage errors
    }
    return null;
  }

  private writeCacheToStorage(data: TenantInfo): void {
    try {
      const key = this.getCacheStorageKey();
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (_) {
      // storage might be unavailable; ignore
    }
  }

  /**
   * Clear in-memory and persistent cache (e.g., on logout or tenant change)
   */
  public invalidateCache(): void {
    this.cachedTenant = null;
    this.cachedAtMs = 0;
    this.inflightRequest = null;
    try {
      const key = this.getCacheStorageKey();
      localStorage.removeItem(key);
    } catch (_) {
      // ignore
    }
  }

  /** Drop in-memory tenant info and every `tenant_info_cache` localStorage key. */
  public clearCache(): void {
    this.cachedTenant = null;
    this.cachedAtMs = 0;
    this.inflightRequest = null;
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith('tenant_info_cache')) {
          keys.push(key);
        }
      }
      for (const key of keys) {
        localStorage.removeItem(key);
      }
    } catch (_) {
      // storage might be unavailable; ignore
    }
  }
  /**
   * Synchronous tenant snapshot from memory or localStorage (5 minute TTL).
   * Lets the UI paint entitlements before the network round-trip.
   */
  public getCachedTenant(): TenantInfo | null {
    if (this.cachedTenant && Date.now() - this.cachedAtMs < this.CACHE_TTL_MS) {
      return this.cachedTenant;
    }
    return this.readCacheFromStorage();
  }

  /**
   * Get current tenant information
   */
  async getCurrentTenant(forceRefresh: boolean = false): Promise<TenantInfo | null> {
    try {
      // Return in-memory cache if fresh and no force refresh requested
      if (!forceRefresh && this.cachedTenant && (Date.now() - this.cachedAtMs < this.CACHE_TTL_MS)) {
        return this.cachedTenant;
      }

      // Try persistent cache if memory cache is empty or stale
      if (!forceRefresh && (!this.cachedTenant || (Date.now() - this.cachedAtMs >= this.CACHE_TTL_MS))) {
        const stored = this.readCacheFromStorage();
        if (stored) return stored;
      }

      // Coalesce concurrent requests
      if (this.inflightRequest) {
        return this.inflightRequest;
      }

      const headers = getAuthHeaders();
      this.inflightRequest = fetchWithAuth(`${API_BASE_URL}/api/getCurrentTenant`, {
          method: 'GET',
          headers,
        })
        .then(async (response) => {
          if (!response.ok) {
            console.error('Failed to fetch tenant information:', response.statusText);
            return null;
          }
          const tenantData = await response.json();
          // Update caches. `entitlements` is kept when the API sends it.
          if (tenantData) {
            const stored: TenantInfo = {
              id: tenantData.id,
              name: tenantData.name,
              subdomain: tenantData.subdomain,
              description: tenantData.description,
              entitlements: Array.isArray(tenantData.entitlements)
                ? tenantData.entitlements.filter(
                    (key: unknown): key is string => typeof key === 'string'
                  )
                : undefined,
            };
            this.cachedTenant = stored;
            this.cachedAtMs = Date.now();
            this.writeCacheToStorage(stored);
            return stored;
          }
          return null;
        })
        .finally(() => {
          // clear inflight regardless of outcome to allow future retries
          this.inflightRequest = null;
        });

      return this.inflightRequest;
    } catch (error) {
      console.error('Error fetching tenant information:', error);
      return null;
    }
  }

  /**
   * Get all available tenants
   */
  async getAllTenants(): Promise<TenantInfo[]> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/getTenants`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.error('Failed to fetch tenants:', response.statusText);
        return [];
      }

      const tenants = await response.json();
      return tenants;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      return [];
    }
  }

  /**
   * Get specific tenant by ID
   */
  async getTenantById(id: string): Promise<TenantInfo | null> {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/getTenant?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.error('Failed to fetch tenant:', response.statusText);
        return null;
      }

      const tenant = await response.json();
      return tenant;
    } catch (error) {
      console.error('Error fetching tenant:', error);
      return null;
    }
  }
}

const tenantService = new TenantService();
export default tenantService; 